import { useState, useCallback } from "react";
import apiService from "@/services/api";

export type ValidationStatus = "idle" | "validating" | "valid" | "invalid";

export interface ValidationResult {
  status: ValidationStatus;
  message?: string;
  details?: string[];
  /** Форматированное значение (например, телефон в формате 8(XXX)XXX-XX-XX) */
  formattedValue?: string;
}

export interface FieldValidations {
  [fieldId: string]: ValidationResult;
}

/**
 * Хук для валидации полей формы через API.
 */
export function useFieldValidation() {
  const [validations, setValidations] = useState<FieldValidations>({});

  const setFieldValidation = useCallback((fieldId: string, result: ValidationResult) => {
    setValidations((prev) => ({ ...prev, [fieldId]: result }));
  }, []);

  const clearFieldValidation = useCallback((fieldId: string) => {
    setValidations((prev) => {
      const newValidations = { ...prev };
      delete newValidations[fieldId];
      return newValidations;
    });
  }, []);

  const validatePhone = useCallback(
    async (phone: string, fieldKey: string = "phone") => {
      if (!phone || phone.length < 18) return; // +7 (XXX) XXX-XX-XX = 18 chars

      setFieldValidation(fieldKey, { status: "validating" });

      try {
        const result = await apiService.validatePhone({ phone, enable_db: true });

        if (result.success && result.data) {
          const details: string[] = [];
          if (result.data.provider) details.push(`Оператор: ${result.data.provider}`);
          if (result.data.region) details.push(`Регион: ${result.data.region}`);
          if (result.data.city) details.push(`Город: ${result.data.city}`);

          setFieldValidation(fieldKey, {
            status: "valid",
            message: result.data.provider || "Номер найден в базе",
            details,
            formattedValue: result.data.value_formatted_8 || undefined,
          });
        } else {
          setFieldValidation(fieldKey, {
            status: "invalid",
            message: result.error || "Номер не найден в базе",
          });
        }
      } catch {
        setFieldValidation(fieldKey, {
          status: "invalid",
          message: "Ошибка проверки номера",
        });
      }
    },
    [setFieldValidation],
  );

  const validateEmail = useCallback(
    async (email: string, fieldKey: string = "email") => {
      if (!email || !email.includes("@")) return;

      setFieldValidation(fieldKey, { status: "validating" });

      try {
        const result = await apiService.validateEmail({
          email,
          check_mx: true,
          check_existence: false,
        });

        if (result.success && result.data) {
          const isValid = result.data.is_valid_format && result.data.has_mx;
          const details: string[] = [];

          if (result.data.domain_type) details.push(`Тип: ${result.data.domain_type}`);
          if (result.data.is_disposable) details.push("Одноразовый домен");
          if (!result.data.has_mx) details.push("Нет MX записей");

          setFieldValidation(fieldKey, {
            status: isValid ? "valid" : "invalid",
            message: isValid ? "Email валиден" : "Проблемы с email",
            details,
          });
        } else {
          setFieldValidation(fieldKey, {
            status: "invalid",
            message: result.error || "Невалидный email",
          });
        }
      } catch {
        setFieldValidation(fieldKey, {
          status: "invalid",
          message: "Ошибка проверки email",
        });
      }
    },
    [setFieldValidation],
  );

  const validateInn = useCallback(
    async (taxId: string) => {
      if (!taxId || (taxId.length !== 10 && taxId.length !== 12)) return;

      setFieldValidation("inn", { status: "validating" });

      try {
        const result = await apiService.validateTaxId({
          tax_id: taxId,
          include_region_info: true,
          include_fns_inspection_info: true,
        });

        if (result.success && result.data) {
          const isValid = result.data.is_valid;
          const details: string[] = [];

          if (result.data.region) details.push(`Регион: ${result.data.region}`);
          if (result.data.fns_inspection) details.push(`ИФНС: ${result.data.fns_inspection}`);
          if (result.data.fns_inspection_address) details.push(`Адрес: ${result.data.fns_inspection_address}`);

          setFieldValidation("inn", {
            status: isValid ? "valid" : "invalid",
            message: isValid ? result.data.region || "ИНН валиден" : result.data.issues?.[0] || "Неверный ИНН",
            details: isValid ? details : undefined,
          });
        } else {
          setFieldValidation("inn", {
            status: "invalid",
            message: result.error || "Неверный ИНН",
          });
        }
      } catch {
        setFieldValidation("inn", {
          status: "invalid",
          message: "Ошибка проверки ИНН",
        });
      }
    },
    [setFieldValidation],
  );

  const validateSnils = useCallback(
    async (snils: string) => {
      const digits = snils.replace(/[-\s]/g, "");
      if (!digits || digits.length !== 11) return;

      setFieldValidation("snils", { status: "validating" });

      try {
        const result = await apiService.validateSnils({
          social_security_number: snils,
          include_info: true,
        });

        if (result.success && result.data) {
          const isValid = result.data.validity;

          setFieldValidation("snils", {
            status: isValid ? "valid" : "invalid",
            message: isValid ? "СНИЛС валиден" : "Неверный СНИЛС",
          });
        } else {
          setFieldValidation("snils", {
            status: "invalid",
            message: result.error || "Неверный СНИЛС",
          });
        }
      } catch {
        setFieldValidation("snils", {
          status: "invalid",
          message: "Ошибка проверки СНИЛС",
        });
      }
    },
    [setFieldValidation],
  );

  const validatePassport = useCallback(
    async (series: string, number: string) => {
      if (!series || !number || series.length !== 4 || number.length !== 6) return;

      setFieldValidation("passport", { status: "validating" });

      try {
        const result = await apiService.validateDocument({
          internal_passport: { document: `${series}${number}` },
          include_region_info: true,
          include_year_info: true,
        });

        if (result.success && result.data) {
          const isValid = result.data.is_valid;
          const details: string[] = [];

          if (result.data.region_name) details.push(`Регион: ${result.data.region_name}`);
          if (result.data.year_code) details.push(`Год бланка: ${result.data.year_code}`);

          setFieldValidation("passport", {
            status: isValid ? "valid" : "invalid",
            message: isValid
              ? result.data.region_name || "Паспорт валиден"
              : result.data.message || "Неверные данные паспорта",
            details: isValid ? details : undefined,
          });
        } else {
          setFieldValidation("passport", {
            status: "invalid",
            message: result.error || "Неверные данные паспорта",
          });
        }
      } catch {
        setFieldValidation("passport", {
          status: "invalid",
          message: "Ошибка проверки паспорта",
        });
      }
    },
    [setFieldValidation],
  );

  const validateDriversLicense = useCallback(
    (seriesNumber: string) => {
      // Формат: "XX YY NNNNNN" (12 символов) или "XXYY NNNNNN" (11 символов)
      // где XX - код региона, YY - буквы/цифры серии, NNNNNN - номер
      if (!seriesNumber) return;

      // Убираем все пробелы для нормализации
      const normalized = seriesNumber.replace(/\s+/g, "");
      if (normalized.length !== 10) return; // 4 серия + 6 номер = 10

      const series = normalized.slice(0, 4);
      const number = normalized.slice(4, 10);

      // Проверяем серию: первые 2 - цифры, следующие 2 - цифры или русские буквы
      const russianLetters = "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ";
      const seriesPattern = new RegExp(`^\\d{2}[\\d${russianLetters}]{2}$`);

      if (!seriesPattern.test(series)) {
        setFieldValidation("drivers_license", {
          status: "invalid",
          message: "Неверный формат серии ВУ",
        });
        return;
      }

      // Проверяем номер: только 6 цифр
      if (!/^\d{6}$/.test(number)) {
        setFieldValidation("drivers_license", {
          status: "invalid",
          message: "Номер ВУ должен содержать 6 цифр",
        });
        return;
      }

      // Определяем тип серии
      const hasLetters = /[А-Я]/.test(series.slice(2));
      const seriesType = hasLetters ? "Старый формат (с буквами)" : "Новый формат (только цифры)";

      setFieldValidation("drivers_license", {
        status: "valid",
        message: "ВУ валидно",
        details: [`Серия: ${seriesType}`],
      });
    },
    [setFieldValidation],
  );

  const validateName = useCallback(
    async (fullName: string) => {
      if (!fullName || fullName.trim().split(/\s+/).length < 2) return;

      setFieldValidation("name", { status: "validating" });

      try {
        const result = await apiService.validateName({
          full_name: fullName,
          normalize_input: true,
          include_validation: true,
        });

        if (result.success && result.data) {
          const details: string[] = [];

          if (result.data.gender === "male") details.push("Мужской");
          else if (result.data.gender === "female") details.push("Женский");

          if (result.data.full_name) details.push(result.data.full_name);

          setFieldValidation("name", {
            status: "valid",
            message:
              result.data.gender === "male"
                ? "Мужское имя"
                : result.data.gender === "female"
                  ? "Женское имя"
                  : "Имя распознано",
            details,
          });
        } else {
          setFieldValidation("name", {
            status: "invalid",
            message: result.error || "Проверьте правильность ввода ФИО",
          });
        }
      } catch {
        setFieldValidation("name", {
          status: "invalid",
          message: "Ошибка проверки ФИО",
        });
      }
    },
    [setFieldValidation],
  );

  /** Кадастровые данные от DaData для передачи в Pullenti */
  interface CadastralData {
    stead_cadnum?: string | null;
    house_cadnum?: string | null;
    flat_cadnum?: string | null;
    flat_area?: string | null;
    square_meter_price?: string | null;
    flat_price?: string | null;
  }

  const validateAddress = useCallback(
    async (address: string, fieldKey: string = "address", cadastralData?: CadastralData) => {
      if (!address || address.length < 5) return;

      setFieldValidation(fieldKey, { status: "validating" });

      try {
        const result = await apiService.validateAddress({
          address,
          cadastral_data: cadastralData
            ? {
                stead_cadnum: cadastralData.stead_cadnum,
                house_cadnum: cadastralData.house_cadnum,
                flat_cadnum: cadastralData.flat_cadnum,
                flat_area: cadastralData.flat_area,
                square_meter_price: cadastralData.square_meter_price,
                flat_price: cadastralData.flat_price,
              }
            : undefined,
        });

        if (result.success && result.data) {
          const validationResult = result.data.validation_result;
          const addressDE = result.data.address;
          const details: string[] = [];

          // Формируем детали из распознанных компонентов
          if (addressDE?.region?.value) {
            details.push(`Регион: ${addressDE.region.value}`);
          }
          if (addressDE?.city?.value) {
            details.push(`Город: ${addressDE.city.value}`);
          }
          if (addressDE?.settlement?.value) {
            details.push(`Населённый пункт: ${addressDE.settlement.value}`);
          }

          // Добавляем кадастровый номер если есть (от Pullenti или DaData)
          const cadNum =
            addressDE?.flat?.cadastrial_number ||
            addressDE?.house?.cadastrial_number ||
            addressDE?.stead?.cadastrial_number ||
            cadastralData?.flat_cadnum ||
            cadastralData?.house_cadnum ||
            cadastralData?.stead_cadnum;
          if (cadNum) {
            details.push(`Кадастровый №: ${cadNum}`);
          }

          // Добавляем площадь если есть (от Pullenti или DaData)
          const area = addressDE?.flat?.area || cadastralData?.flat_area;
          if (area) {
            details.push(`Площадь: ${area} м²`);
          }

          // Добавляем стоимость если есть
          if (cadastralData?.flat_price) {
            const price = parseFloat(cadastralData.flat_price);
            if (!isNaN(price)) {
              const formattedPrice = new Intl.NumberFormat("ru-RU").format(price);
              details.push(`Стоимость: ${formattedPrice} руб.`);
            }
          }

          // Добавляем качество распознавания
          if (result.data.metrics) {
            const qualityPercent = Math.round(result.data.metrics.quality_score * 100);
            if (qualityPercent >= 80) {
              details.push(`Качество: ${qualityPercent}%`);
            }
          }

          const isValid = validationResult.is_valid;
          setFieldValidation(fieldKey, {
            status: isValid ? "valid" : "invalid",
            message: isValid
              ? addressDE?.pullenti_standardized || "Адрес распознан"
              : validationResult.issues?.[0] || "Адрес не распознан",
            details: details.length > 0 ? details : undefined,
          });
        } else {
          setFieldValidation(fieldKey, {
            status: "invalid",
            message: result.error || "Ошибка валидации адреса",
          });
        }
      } catch {
        setFieldValidation(fieldKey, {
          status: "invalid",
          message: "Ошибка проверки адреса",
        });
      }
    },
    [setFieldValidation],
  );

  return {
    validations,
    setFieldValidation,
    clearFieldValidation,
    validatePhone,
    validateEmail,
    validateInn,
    validateSnils,
    validatePassport,
    validateDriversLicense,
    validateName,
    validateAddress,
  };
}
